import { inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import moment from 'moment-timezone';
import { Observable, switchMap } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { BillingPlanTier, type CancelPayload, Subscription } from '@core/billing-types';
import { ChurnFlowBaseHttpService } from '@core/churn-flow-data';
import { ChurnFlowDataBaseService } from '@core/churn-flow-logic';
import { ChurnReason, SubscriptionEndDateI } from '@core/churn-flow-types';
import { filterDefined } from '@core/common-legacy';
import { TellentCurrentUserSelectors } from '@core/tellent-data';
import { AppKind, AppRecruitment, TellentMember } from '@core/tellent-types';
import { injectTellentAppUrlOpener } from '@core/tellent-util';
import { SubscriptionPricingActions } from '@recruitee/billing-data';

import { SubscriptionDataService } from '../../subscription-data.service';

@Injectable()
export class ChurnFlowDataService extends ChurnFlowDataBaseService {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly subscriptionDataService = inject(SubscriptionDataService);
  private readonly tellentAppUrlOpener = injectTellentAppUrlOpener();
  private readonly currentMembership = inject(Store).select(
    TellentCurrentUserSelectors.currentOrganizationMembershipSelectorSafe,
  );
  private readonly churnFlowHttp = inject(ChurnFlowBaseHttpService);

  public subscriptionCancellationDates: Signal<SubscriptionEndDateI | undefined> = toSignal(
    this.subscriptionDataService.data$.pipe(map(sub => this.calculateCancellationDates(sub))),
  );

  public displayDownsellSolution: Signal<boolean | undefined> = toSignal(
    this.subscriptionDataService.data$.pipe(
      filterDefined,
      map(sub => sub.tier !== BillingPlanTier.Launch),
    ),
  );

  public careersSiteUrl: Signal<string | undefined> = toSignal(
    this.currentMembership.pipe(
      filterDefined,
      map(
        (membership: TellentMember) =>
          membership?.seats.find(seat => seat.app.kind === 'recruitment')?.app,
      ),
      map((app: AppRecruitment) =>
        app.data.careersSite?.enabled ? `https://${app.data.careersSite?.url}` : undefined,
      ),
    ),
  );

  public displayRenewOnceNote: Signal<boolean | undefined> = toSignal(
    this.subscriptionDataService.data$.pipe(
      filterDefined,
      map((sub: Subscription) => sub.contractTerm?.actionAtTermEnd === 'renew_once'),
    ),
  );

  public lockedChurnReason: Observable<ChurnReason | undefined> =
    this.subscriptionDataService.data$.pipe(
      filterDefined,
      map((sub: Subscription) => sub.lockedChurnReason),
    );

  public activateTellentHrFreeTrial(): void {
    this.tellentAppUrlOpener(AppKind.TellentAdmin, { id: 'billing' });
  }

  public cancelSubscription(): Observable<any> {
    return this.lockedChurnReason.pipe(
      filterDefined,
      switchMap((selectedReason: ChurnReason) => {
        const cancelPayload: CancelPayload = {
          reason: selectedReason,
        };
        this.store.dispatch(SubscriptionPricingActions.cancel({ payload: cancelPayload }));
        return this.actions$.pipe(
          ofType(SubscriptionPricingActions.cancelSuccess),
          take(1),
          map(res => res.payload),
        );
      }),
    );
  }

  public lockInChurnReason(reason: ChurnReason): Observable<ChurnReason> {
    return this.churnFlowHttp.setChurnReason(reason, undefined);
  }

  public refreshSubscriptionData(): void {
    this.subscriptionDataService.fetch();
  }

  private calculateCancellationDates(subscription: Subscription): SubscriptionEndDateI {
    const dateFormat = 'MMMM DD, YYYY';
    const { contractEnd, cancellationCutoffPeriod } = subscription.contractTerm || {};

    // If contract terms apply, use them to calculate end date and latest cancellation date
    if (contractEnd && cancellationCutoffPeriod) {
      return {
        endDate: moment(contractEnd).format(dateFormat),
        latestCancellationDate: moment(contractEnd)
          .subtract(cancellationCutoffPeriod, 'days')
          .format(dateFormat),
      };
    }

    // If there are no contract terms, use the current period end to calculate end date and latest cancellation date
    return {
      endDate: moment(subscription.currentPeriodEnd).format(dateFormat),
      latestCancellationDate: moment(subscription.currentPeriodEnd)
        .subtract(1, 'days')
        .format(dateFormat),
    };
  }
}
